import { ProgressBar } from 'react-native-paper';
import { Image, ImageStyle, TouchableOpacity, View } from 'react-native';
import React, { useEffect, useState } from 'react';

import { Map } from '@components/Map';
import { Modal } from '@components/Modal';
import { OrderDriver } from '@lib/types';
import { UserDetailsRow } from '@components/OrderView/UserDetails/UserDetailsRow';
import { crashlyticsUtil } from '@lib/crashlytics';
import { useIsMobile } from '@hooks';
import { useTranslation } from '@providers/I18n';
import { Direction, useDirection } from '@providers/Direction';
import { SizeValue, Typography } from '@components/Typography';
import { makeStyles, useTheme } from '@providers/Theme';

export type DriverInfoProps = {
  showTracking: boolean;
  isSelected?: boolean;
  driver: OrderDriver;
  textSize?: SizeValue;
};

type DriverInfoPStylesProps = {
  direction: Direction;
  isSelected?: boolean;
};

const useStyles = makeStyles<DriverInfoPStylesProps>((theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 60,
    height: 60,
    maxWidth: 60,
    maxHeight: 60,
    flex: 2,
    borderRadius: 50,
    marginRight: 16,
  },
  content: (props) => ({
    flex: 3,
    color: props.isSelected ? 'white' : theme.palette.base[900],
  }),
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  name: {
    flex: 1,
    color: theme.palette.ocean[700],
  },
  track: {
    flex: 4,
    color: theme.palette.ocean[700],
  },
  progress: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[500],
    marginVertical: theme.spacing(1),
  },
  icon: {
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  state: (props) => ({
    flex: 1,
    textAlign: props.direction === 'rtl' ? 'right' : 'left',
    color: props.isSelected ? 'white' : theme.palette.base[900],
  }),
  time: (props) => ({
    flex: 1,
    textAlign: props.direction === 'rtl' ? 'left' : 'right',
    color: props.isSelected ? 'white' : theme.palette.base[900],
  }),
}));

export const DriverInfo: React.FC<DriverInfoProps> = ({
  showTracking,
  textSize,
  isSelected,
  driver,
}) => {
  const { direction } = useDirection();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const theme = useTheme();

  const styles = useStyles({ direction, isSelected });
  const [userMapOpen, setUserMapOpen] = useState(false);

  /* ---------------------------------------------------------------------- */
  /*                          CRASHLYTICS DEBUG LOGS                        */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    crashlyticsUtil.log('DriverInfo mounted');

    crashlyticsUtil.setAttributes({
      driver_name: driver?.name ?? 'null',
      trip_status: driver?.tripStatus ?? 'null',
      has_photo: driver?.photo ? 'true' : 'false',
    });

    return () => {
      crashlyticsUtil.log('DriverInfo unmounted');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estimatedMovingDuration = driver?.estimatedMovingDuration ?? 0;
  const expectedRemainingTime = driver?.expectedRemainingTime ?? 0;

  const progress =
    estimatedMovingDuration > 0
      ? Math.max(
          0,
          Math.min(
            1,
            1 - expectedRemainingTime / estimatedMovingDuration
          )
        )
      : 0;

  return (
    <View style={styles.root} testID="driver-info">
      <View style={styles.container}>
        {(showTracking && !!driver.photo) && (
          <Image
            testID="driver-photo"
            style={styles.avatar as ImageStyle}
            source={{
              uri: driver.photo,
            }}
          />
        )}

        <View style={styles.content}>
          <Typography weight="light" size={textSize ?? 'sm'}>
            {t('orderDetails.userDetails.yourDriver', {
              defaultValue: 'Your driver {{value}} is ',
              value: driver.name,
            })}
            {driver.tripStatus === 'READY_FOR_PICKUP'
              ? t(
                  'orderDetails.userDetails.driverState.readyForPickup',
                  'ready for pickup',
                )
              : t(
                  'orderDetails.userDetails.driverState.onTheWay',
                  'on the way.',
                )}
          </Typography>

          {showTracking && (
            <View style={styles.content} testID="driver-name">
              <View style={styles.container}>
                <View style={styles.name}>
                  <UserDetailsRow
                    icon="phone"
                    iconProps={{ color: theme.palette.ocean[700], size: 16 }}
                    value={
                      <Typography weight="regular">
                        {driver.phoneNumber}
                      </Typography>
                    }
                  />
                </View>

                <View
                  testID="track-btn"
                  style={!isMobile ? styles.track : styles.name}
                >
                  <TouchableOpacity onPress={() => setUserMapOpen(true)}>
                    <UserDetailsRow
                      icon="pin"
                      value={
                        <Typography weight="regular">
                          {t('orderDetails.driverInfo.track', 'Track')}
                        </Typography>
                      }
                      iconProps={{
                        color: theme.palette.ocean[700],
                        size: 16,
                      }}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>

      <ProgressBar
        progress={progress}
        color={isSelected ? 'white' : theme.palette.primary[600]}
        style={styles.progress}
      />

      <View style={styles.container}>
        <Typography
          weight="medium"
          size={textSize ?? 'sm'}
          style={styles.state}
        >
          {t('orderDetails.driverInfo.state', 'Arriving in:')}
        </Typography>

        <Typography weight="medium" size={textSize ?? 'sm'} style={styles.time}>
          {((driver?.expectedRemainingTime ?? 0) / 60).toFixed(0)}{' '}
          {t('orderDetails.driverInfo.minutes', 'mins')}
        </Typography>
      </View>

      <Modal
        open={userMapOpen}
        onRequestClose={() => setUserMapOpen(false)}
        dismissOnBackdropPress
        animationType="fade"
      >
        <View style={styles.modalContent}>
          {!!driver.photo && (
            <Image
              testID="driver-photo"
              style={styles.avatar as ImageStyle}
              source={{
                uri: driver.photo,
              }}
            />
          )}

          <Typography size="2xl" weight="semiBold" style={styles.title}>
            {t('driverMap.title', {
              defaultValue: "{{value}}'s Location",
              value: driver.name,
            })}
          </Typography>

          <Map location={driver.location} />
        </View>
      </Modal>
    </View>
  );
};
